import { AddressValidatorComponent } from 'src/app/shared/address-validator/address-validator.component';
import { HelperService } from 'src/app/core/service/helper.service';
import { Dictionary, Code } from './../../core/model/dictionary';
import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { ProviderEdit } from '../model/provider-edit';
import { Observable } from 'rxjs';
import { ProviderValidators } from 'src/app/core/validators/provider.validators';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { Guid } from 'guid-typescript';
import { FacilityService } from 'src/app/core/service/facility.service';
import { FacilityEdit } from 'src/app/facility/model/facility-edit';

@Component({
  standalone: false,
  selector: 'app-provider-edit',
  templateUrl: './provider-edit.component.html',
  styleUrls: ['./provider-edit.component.scss']
})
export class ProviderEditComponent implements OnInit, AfterViewChecked
{
  @ViewChild('addressValidator') public addressValidator: AddressValidatorComponent;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<ProviderEdit> = new EventEmitter();

  public urlPattern = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  /**
   * All language codes
   */
  public allLanguages = [];

  /**
   * All facilities
   */
  public allFacilities = [];

  /**
   * Filtered list of languages for drop-down.
   */
  public languagesFiltered: Dictionary[] = [];

  /**
   * Filtered list of facilities for drop-down.
   */
  public facilitiesFiltered: FacilityEdit[] = [];

  public editForm: any;
  public editFormActive = false;
  public genders = [];

  /**
   * All specialty codes
   */
  public allSpecialties = [];

  /**
   * Filtered list of specialties for drop-down.
   */
  public specialtiesFiltered: Dictionary[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for specialty virtualization. This is to accelerate the DOM performance.
   */
  public virtualSpecialty: any = { itemHeight: 30 };

  /**
   * Maximum number of items for facility virtualization. This is to accelerate the DOM performance.
   */
  public virtualFacility: any = { itemHeight: 30 };

  public states: Code[] = [];
  public countries: Code[] = [];
  public phoneNumberMask: string;
  public addressChanged = false;
  public addressAPIEnabled = false;
  public saving = false;
  public providerToSave: any;

  private providerEdit: ProviderEdit;

  constructor(
    private dictionaryService: DictionaryService,
    private changeDetectorRef: ChangeDetectorRef,
    private helperService: HelperService,
    private fb: UntypedFormBuilder,
    private providerValidators: ProviderValidators,
    private messageHandlerService: MessageHandlerService,
    private facilityService: FacilityService,
    private settingService: SettingService)
  {
    this.loadLookups();
  }

  public get longitude(): UntypedFormControl
  {
    return this.editForm.get('longitude');
  }

  public get latitude(): UntypedFormControl
  {
    return this.editForm.get('latitude');
  }

  public get firstName(): UntypedFormControl
  {
    return this.editForm.get('firstName');
  }

  public get lastName(): UntypedFormControl
  {
    return this.editForm.get('lastName');
  }

  public get middleName(): UntypedFormControl
  {
    return this.editForm.get('middleName');
  }

  public get npi(): UntypedFormControl
  {
    return this.editForm.get('npi');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get streetLine(): UntypedFormControl
  {
    return this.editForm.get('streetLine');
  }

  public get streetLineAdditional(): UntypedFormControl
  {
    return this.editForm.get('streetLineAdditional');
  }

  public get city(): UntypedFormControl
  {
    return this.editForm.get('city');
  }

  public get county(): UntypedFormControl
  {
    return this.editForm.get('county');
  }

  public get postalCode(): UntypedFormControl
  {
    return this.editForm.get('postalCode');
  }

  public get state(): UntypedFormControl
  {
    return this.editForm.get('state');
  }

  public get country(): UntypedFormControl
  {
    return this.editForm.get('country');
  }

  public get organization(): UntypedFormControl
  {
    return this.editForm.get('organization');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  public get genderId(): UntypedFormControl
  {
    return this.editForm.get('genderId');
  }

  public get professionalSuffix(): UntypedFormControl
  {
    return this.editForm.get('professionalSuffix');
  }

  public get suffix(): UntypedFormControl
  {
    return this.editForm.get('suffix');
  }

  public get title(): UntypedFormControl
  {
    return this.editForm.get('title');
  }

  public get directAddress(): UntypedFormControl
  {
    return this.editForm.get('directAddress');
  }

  public get specialtyId(): UntypedFormControl
  {
    return this.editForm.get('specialtyId');
  }

  public get qualityScore(): UntypedFormControl
  {
    return this.editForm.get('qualityScore');
  }

  public get email(): UntypedFormControl
  {
    return this.editForm.get('email');
  }

  public get faxNumber(): UntypedFormControl
  {
    return this.editForm.get('faxNumber');
  }

  public get telephoneNumber(): UntypedFormControl
  {
    return this.editForm.get('telephoneNumber');
  }

  public get telephoneNumberExtension(): UntypedFormControl
  {
    return this.editForm.get('telephoneNumberExtension');
  }

  public get cellPhoneNumber(): UntypedFormControl
  {
    return this.editForm.get('cellPhoneNumber');
  }

  public get languages(): UntypedFormControl
  {
    return this.editForm.get('languages');
  }

  public get facilities(): UntypedFormControl
  {
    return this.editForm.get('facilities');
  }

  @Input() public set model(providerEdit: ProviderEdit)
  {
    this.initModel(providerEdit);
  }

  public ngOnInit(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.addressAPIEnabled = sessionSetting.addressAPIEnabled;
      }
    });
  }

  public ngAfterViewChecked(): void
  {
    this.changeDetectorRef.detectChanges();
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    if ((this.firstName.value === '' && this.lastName.value === '' && this.organization.value === '') ||
      ((this.firstName.value !== '' && this.lastName.value === '') || (this.firstName.value === '' && this.lastName.value !== '')))
    {
      this.messageHandlerService.displayError('REQUIRED-PROVIDER-ORGANIZATION-OR-NAME');
      return;
    }

    const objectToPersist = this.editForm.value;

    // Languages
    objectToPersist.languages = this.languages.value;

    // Facilities
    objectToPersist.facilities = this.facilities.value;

    // Set address object and clean up
    this.setAddress(objectToPersist);

    this.performProviderSave(objectToPersist);
  }

  /**
   * Perform the Provide Save.
   */
  public saveProvider(): void
  {
    // Emit save event
    this.save.emit(this.providerToSave);
    this.addressChanged = false;
    this.saving = false;
    this.editFormActive = false;
  }


  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Address change event from Address Validator component.
   *
   * @param event Address data being selected to apply.
   */
  public changeAddress(event: any): void
  {
    // Override the address based on Address API response.
    if (event.confirmation === 'recommended')
    {
      // Force dirty to change state
      this.editForm.markAsDirty();

      // Set form properties
      this.streetLine.setValue(event.line1);
      this.streetLineAdditional.setValue(event.line2);
      const state = event.postalAddress && event.postalAddress.administrativeArea ?
        this.states.find(x => x.code === event.postalAddress.administrativeArea) : null;
      this.state.setValue(state !== null ? state.code : null);
      this.city.setValue(event.postalAddress && event.postalAddress.locality ? event.postalAddress.locality : null);
      this.postalCode.setValue(event.postalAddress && event.postalAddress.postalCode ? event.postalAddress.postalCode : null);
      this.latitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.latitude : null);
      this.longitude.setValue(event.geocode && event.geocode.location ? event.geocode.location.longitude : null);

      if (event.postalAddress && event.postalAddress.regionCode)
      {
        const country = this.countries.find(x => x.alternateCode === event.postalAddress.regionCode);
        this.country.setValue(country ? country.code : null);
      }
      this.addressChanged = false;    // Mark address unchanged to avoid opening modal again unless it is manually changed again.
    }
    else
    {
      this.latitude.setValue(null);
      this.longitude.setValue(null);
    }

    // If autoSave, then set patient object and save.
    if (event.autoSave)
    {
      this.setAddress(this.providerToSave);
      this.saveProvider();
    }
  }

  /**
   * Manual Address Validation. No autoSave will be performed.
   */
  public validateAddress(): void
  {
    this.addressValidator.execute(false, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
      this.state.value, this.country.value, this.postalCode.value);
  }

  /**
   * Filters the specialties based on user input.
   */
  public filterSpecialties(filter: string): void
  {
    this.searchTerm = filter;
    this.specialtiesFiltered = this.allSpecialties
      .filter(specialty => specialty.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  public onAssignNPI(): void
  {
    this.npi.setValue(Guid.create().toString());
    this.editForm.markAsDirty();
  }

  /**
   * Filters the languages based on user input.
   */
  public filterLanguages(filter: string): void
  {
    this.searchTerm = filter;
    this.languagesFiltered = this.allLanguages
      .filter(lang => lang.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Filters the facilities based on user input.
   */
  public filterFacilities(filter: string): void
  {
    this.searchTerm = filter;
    this.facilitiesFiltered = this.allFacilities
      .filter(fa => fa.name.toLowerCase().includes(filter.toLowerCase()));
  }

  public isValidUrl(): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return this.url.value !== undefined && this.url.value && regEx.test(this.url.value);
  }

  public openUrl(): void
  {
    if (this.isValidUrl())
    {
      window.open(this.url.value);
    }
  }

  private setAddress(objectToPersist: any): void
  {
    // Init object if null
    if (objectToPersist.practiceAddress === null || objectToPersist.practiceAddress === undefined)
    {
      objectToPersist.practiceAddress = {};
    }

    objectToPersist.practiceAddress.streetLine = this.streetLine && this.streetLine.value !== '' ? this.streetLine.value : null;
    objectToPersist.practiceAddress.streetLineAdditional = this.streetLineAdditional && this.streetLineAdditional.value !== '' ?
      this.streetLineAdditional.value : null;
    objectToPersist.practiceAddress.city = this.city && this.city.value !== '' ? this.city.value : null;
    objectToPersist.practiceAddress.county = this.county && this.county.value !== '' ? this.county.value : null;
    objectToPersist.practiceAddress.postalCode = this.postalCode && this.postalCode.value !== '' ? this.postalCode.value : null;
    objectToPersist.practiceAddress.state = this.state && this.state.value ? this.state.value : null;
    objectToPersist.practiceAddress.country = this.country && this.country.value ? this.country.value : null;
    objectToPersist.practiceAddress.longitude = this.longitude && this.longitude.value !== '' ? this.longitude.value : null;
    objectToPersist.practiceAddress.latitude = this.latitude && this.latitude.value !== '' ? this.latitude.value : null;

    delete objectToPersist.streetLine;
    delete objectToPersist.streetLineAdditional;
    delete objectToPersist.city;
    delete objectToPersist.county;
    delete objectToPersist.postalCode;
    delete objectToPersist.state;
    delete objectToPersist.country;
    delete objectToPersist.latitude;
    delete objectToPersist.longitude;
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private loadLookups(): void
  {
    this.dictionaryService.getCachedSpecialties().subscribe({
      next: (data) =>
      {
        this.allSpecialties = data;
        this.filterSpecialties('');
      }
    });

    this.helperService.getGendersStatesCountriesFromMultipleSources().subscribe({
      next: (responseList) =>
      {
        this.genders = responseList[0];
        this.states = responseList[1];
        this.countries = responseList[2];
      }
    });

    // Get languages
    this.dictionaryService.getCachedLanguages().subscribe({
      next: (data) =>
      {
        this.allLanguages = data;
        this.filterLanguages('');
      }
    });

    // Get facilities
    this.facilityService.getAllFacilities().subscribe({
      next: (data) =>
      {
        this.allFacilities = data;
        this.filterFacilities('');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  private initModel(providerEdit: ProviderEdit)
  {
    if (providerEdit === undefined)
    {
      return;
    }

    // Create temporary address properties to make a flat properties for the address.
    // This is necessary to not losing values when reseting the form state below.
    providerEdit.streetLine = providerEdit.practiceAddress ? providerEdit.practiceAddress.streetLine : null;
    providerEdit.streetLineAdditional = providerEdit.practiceAddress ?
      providerEdit.practiceAddress.streetLineAdditional : null;
    providerEdit.city = providerEdit.practiceAddress ? providerEdit.practiceAddress.city : null;
    providerEdit.county = providerEdit.practiceAddress ? providerEdit.practiceAddress.county : null;
    providerEdit.postalCode = providerEdit.practiceAddress ? providerEdit.practiceAddress.postalCode : null;
    providerEdit.state = providerEdit.practiceAddress ? providerEdit.practiceAddress.state : null;
    providerEdit.country = providerEdit.practiceAddress ? providerEdit.practiceAddress.country : null;

    // Set the current npi used for npi async validators
    const previousNpi = this.isNew ? '' : providerEdit.npi;

    // Set form
    this.editForm = this.fb.group({
      id: providerEdit.id,
      version: providerEdit.version,
      firstName: providerEdit.firstName,
      lastName: providerEdit.lastName,
      middleName: providerEdit.middleName,
      npi: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.providerValidators.validateProviderNpiUnique(this.editForm.get('npi'), previousNpi)
        },
      ],
      telephoneNumber: providerEdit.telephoneNumber,
      telephoneNumberExtension: providerEdit.telephoneNumberExtension,
      cellPhoneNumber: providerEdit.cellPhoneNumber,
      faxNumber: providerEdit.faxNumber,
      email: [{ value: providerEdit.email }, Validators.email],
      practiceAddress: providerEdit.practiceAddress,
      genderId: providerEdit.genderId,
      professionalSuffix: providerEdit.professionalSuffix,
      suffix: providerEdit.suffix,
      title: providerEdit.title,
      directAddress: providerEdit.directAddress,
      organization: providerEdit.organization,
      specialtyId: providerEdit.specialtyId,
      qualityScore: providerEdit.qualityScore,
      // Temporary properties below
      streetLine: providerEdit.streetLine,
      streetLineAdditional: providerEdit.streetLineAdditional,
      city: providerEdit.city,
      county: providerEdit.county,
      postalCode: providerEdit.postalCode,
      state: providerEdit.state,
      country: providerEdit.country,
      longitude: null,
      latitude: null,
      languages: [
        { value: providerEdit.languages, disabled: false }],
      facilities: [
        { value: providerEdit.facilities, disabled: false }],
      url: [
        { value: providerEdit.url, disabled: false },
        Validators.pattern(this.urlPattern)
      ],
      description: providerEdit.description,
    });

    this.editForm.reset(providerEdit);
    this.npi.setValue(providerEdit.npi);  // This is a work around to fix asyncValidators

    // Monitor address changes to not show address validator all the time.
    this.addressChanged = false;
    this.editForm.get('streetLine').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('streetLineAdditional').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('city').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('state').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('country').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });
    this.editForm.get('postalCode').valueChanges.subscribe({ next: () => { this.addressChanged = true; } });

    this.providerEdit = providerEdit;
    this.editFormActive = true;
  }

  private performProviderSave(provider: any): void
  {
    this.saving = true;
    this.providerToSave = provider;
    if (this.addressChanged && this.streetLine.value)
    {
      this.addressValidator.execute(true, this.streetLine.value, this.streetLineAdditional.value, this.city.value,
        this.state.value, this.country.value, this.postalCode.value);
    }
    else
    {
      this.saveProvider();
    }
  }
}
